import { ArrowRight, Lock } from 'lucide-react'
import { toast } from 'sonner'
import { patchDealStage } from '@/services/patchDealStage'
import { getFriendlyError } from '@/utils/getFriendlyError'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import type { Deal, DealStage } from '@/types'
import {
  DeleteConfirmationModal,
  FutureDealModal,
  LossConfirmationModal,
  StageChoiceModal,
  ContractDataModal,
  WinDealModal,
} from './Modals'
import type { ContractDataForm, ContractDataModalMode } from './contractDataUtils'

type DealDetailPanelModalsProps = {
  deal: Deal
  showDelete: boolean
  showLoss: boolean
  showFuture: boolean
  showWin: boolean
  showStageChoice: boolean
  showContractData: boolean
  contractDataModalMode: ContractDataModalMode
  contractDataDeal: Deal
  blockedRequirements: string[]
  onCloseDelete: () => void
  onCloseLoss: () => void
  onCloseFuture: () => void
  onCloseWin: () => void
  onCloseStageChoice: () => void
  onCloseContractData: () => void
  onContractDataConfirm: (form: ContractDataForm) => void | Promise<void>
  onClearBlocked: () => void
  onGoToInfo: () => void
  onDealDeleted?: (dealId: string) => void
  onDealPatched: (deal: Deal) => void
  onStageChoice: (stage: DealStage) => void | Promise<void>
}

export function DealDetailPanelModals({
  deal,
  showDelete,
  showLoss,
  showFuture,
  showWin,
  showStageChoice,
  showContractData,
  contractDataModalMode,
  contractDataDeal,
  blockedRequirements,
  onCloseDelete,
  onCloseLoss,
  onCloseFuture,
  onCloseWin,
  onCloseStageChoice,
  onCloseContractData,
  onContractDataConfirm,
  onClearBlocked,
  onGoToInfo,
  onDealDeleted,
  onDealPatched,
  onStageChoice,
}: DealDetailPanelModalsProps) {
  return (
    <>
      <DeleteConfirmationModal
        open={showDelete}
        onClose={onCloseDelete}
        dealId={deal.id}
        dealName={deal.empresaNome}
        onDeleted={onDealDeleted}
      />

      <LossConfirmationModal
        open={showLoss}
        onClose={onCloseLoss}
        dealId={deal.id}
        onLost={async (updates) => {
          try {
            const updated = await patchDealStage(deal.id, 'perdido' as DealStage, updates)
            onDealPatched(updated)
            toast.success('Deal marcado como perdido')
          } catch (err: unknown) {
            toast.error(getFriendlyError(err, 'Erro ao marcar como perdido'))
            throw new Error('loss_stage_failed')
          }
        }}
      />

      <FutureDealModal
        open={showFuture}
        onClose={onCloseFuture}
        dealId={deal.id}
        onScheduled={async (updates) => {
          try {
            const updated = await patchDealStage(deal.id, 'fechamento_futuro' as DealStage, updates)
            onDealPatched(updated)
            toast.success('Deal agendado para o futuro')
          } catch (err: unknown) {
            toast.error(getFriendlyError(err, 'Erro ao agendar fechamento futuro'))
            throw new Error('future_stage_failed')
          }
        }}
      />

      <WinDealModal
        open={showWin}
        onClose={onCloseWin}
        dealId={deal.id}
        defaultDate={deal.dataFechamento}
        defaultObservacoes={deal.observacoes}
        onConfirm={async (updates) => {
          try {
            const updated = await patchDealStage(deal.id, 'ganho' as DealStage, updates)
            onDealPatched(updated)
            toast.success('Deal marcado como ganho')
          } catch (err: unknown) {
            toast.error(getFriendlyError(err, 'Erro ao marcar como ganho'))
            throw new Error('win_stage_failed')
          }
        }}
      />

      <StageChoiceModal
        open={showStageChoice}
        onClose={onCloseStageChoice}
        onSelect={onStageChoice}
      />

      <ContractDataModal
        open={showContractData}
        mode={contractDataModalMode}
        deal={contractDataDeal}
        onClose={onCloseContractData}
        onConfirm={onContractDataConfirm}
      />

      <TransitionBlockModal
        open={blockedRequirements.length > 0}
        onClose={onClearBlocked}
        requirements={blockedRequirements}
        onGoToInfo={onGoToInfo}
      />
    </>
  )
}

function TransitionBlockModal({
  open,
  onClose,
  requirements,
  onGoToInfo,
}: {
  open: boolean
  onClose: () => void
  requirements: string[]
  onGoToInfo: () => void
}) {
  return (
    <Modal open={open} onClose={onClose} title="Antes de avançar" size="sm">
      <div className="space-y-4">
        <div className="flex items-start gap-3 rounded-md border border-amber-400/10 bg-amber-400/5 p-3">
          <Lock size={18} className="mt-0.5 shrink-0 text-amber-400" />
          <p className="text-xs text-text-label">
            Esta transição está bloqueada porque faltam informações obrigatórias para manter o
            histórico comercial consistente.
          </p>
        </div>
        <div className="space-y-2">
          {requirements.map((item) => (
            <div key={item} className="flex items-center gap-2 text-sm text-text-primary">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
              {item}
            </div>
          ))}
        </div>
        <div className="flex gap-2 pt-2">
          <Button variant="secondary" size="sm" onClick={onClose} className="flex-1">
            Fechar
          </Button>
          <Button
            variant="primary"
            size="sm"
            icon={<ArrowRight size={13} />}
            onClick={onGoToInfo}
            className="flex-1"
          >
            Ir para dados
          </Button>
        </div>
      </div>
    </Modal>
  )
}
